import React, { useEffect, useState } from "react";
import { SecureLinkItem } from "../../../components/SecureLinkItem.tsx";
import { SecureLink } from "../../../api/ApiTypes.ts";
import { useAppraiserApi } from "../../../context/ApiContext.tsx";
import { SecureLinkSkeletonItem } from "../../../components/SecureLinkSkeletonItem.tsx";
import { RefreshCw } from "lucide-react";
import { useParams } from "react-router-dom";
import Modal from "../../../components/Modal";
import { SecureLinkDetail } from "./SecureLinkDetail.tsx";

export const SecureLinks: React.FC = () => {
    const { secureLinkId } = useParams();
    const [secureLinks, setSecureLinks] = useState<SecureLink[]>([]);
    const [selectedLink, setSelectedLink] = useState<SecureLink | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const appraiserApi = useAppraiserApi();

    const fetchSecureLinks = async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await appraiserApi.getMySecureLinks();
            setSecureLinks(data);

            // If there's a secure link ID in the URL, find and select that link
            if (secureLinkId) {
                // Convert secureLinkId string to number for comparison
                const linkIdNum = parseInt(secureLinkId, 10);
                const selectedSecureLink = data.find(link => link.id === linkIdNum);
                if (selectedSecureLink) {
                    setSelectedLink(selectedSecureLink);
                    setModalOpen(true);
                } else {
                    console.error(`Secure link with ID ${secureLinkId} not found`);
                    // Optionally show an error message or redirect
                }
            }
        } catch (err) {
            console.error("Failed to fetch secure links:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSecureLinks();
    }, [secureLinkId]); // Re-fetch when the ID in the URL changes

    const closeModal = () => {
        setModalOpen(false);
        setSelectedLink(null);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Secure Links</h1>
                <button
                    onClick={fetchSecureLinks}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors hover:cursor-pointer"
                    disabled={loading}
                >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>
            {loading && (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <SecureLinkSkeletonItem key={i} />
                    ))}
                </div>
            )}

            {error && (
                <div className="text-red-600 text-sm text-center">Failed to load secure links.</div>
            )}

            {!loading && secureLinks.length === 0 && !error && (
                <div className="text-slate-500 text-sm text-center">No secure links found.</div>
            )}

            {!loading && secureLinks.length > 0 && (
                <div className="space-y-4">
                    {secureLinks.map((link) => (
                        <SecureLinkItem
                            key={link.id}
                            secureLink={link}
                            onView={() => {
                                setSelectedLink(link);
                                setModalOpen(true);
                            }}
                        />
                    ))}
                </div>
            )}

            {selectedLink && (
                <Modal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    title="Secure Link Details"
                    maxWidth="2xl"
                >
                    <SecureLinkDetail secureLink={selectedLink} />
                </Modal>
            )}
        </div>
    );
};